import { Injectable } from '@angular/core';
import { AppInjector } from '../../app-injector';
import { AppUserClaimsService } from './app-user-claims.service';
import { AccountsService } from './accounts.service';
import { Account, User } from '@mitel/cloudlink-sdk';
import { AdminService } from './admin.service';
import { AuthService } from './auth.service';
import { MenuItem } from '../interfaces/menu-item'; 
import { UserRoles } from '../constants/constants';
import { SpinnerService } from './spinner.service';
import { OrdersService } from './orders.service';
import { ContactCenterDialogComponent } from '../../modules/integrations-applications/components/integ-config-dialogs/contact-center-dialog/contact-center-dialog.component';
import { ContactCenterRemoveDialogComponent } from '../../modules/integrations-applications/components/integ-config-dialogs/contact-center-remove-dialog/contact-center-remove-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { FreeTrialDialogComponent } from '../../modules/integrations-applications/components/integ-config-dialogs/free-trial-dialog/free-trial-dialog.component';
import { environment } from '../../../environments/environment';

@Injectable({
	providedIn: 'root'
})

// this service will hide whatever implementation we eventually have for gettinng the list of integrations
// types. Right now it is static
export class IntegSharedCCService {
	private appClaimsService: AppUserClaimsService = undefined;
	private accountsService: AccountsService = undefined; 
	private adminService: AdminService = undefined;
	private authService: AuthService = undefined;
	private spinnerService: SpinnerService  = undefined;
	private orderService: OrdersService = undefined;
	private translateService: TranslateService = undefined;
	public dialog: MatDialog;

	constructor(  ) 
	{
	}

	initServices()
	{
		if ( !this.appClaimsService )
			this.appClaimsService = AppInjector.injector.get(AppUserClaimsService);

		if ( !this.accountsService )
			this.accountsService = AppInjector.injector.get(AccountsService);

		if ( !this.adminService )
			this.adminService = AppInjector.injector.get(AdminService);

		if ( !this.authService )
			this.authService = AppInjector.injector.get(AuthService);

		if ( !this.spinnerService )
			this.spinnerService = AppInjector.injector.get(SpinnerService);

		if ( !this.orderService )
			this.orderService = AppInjector.injector.get(OrdersService);

		if ( !this.translateService )
			this.translateService = AppInjector.injector.get(TranslateService);

		if ( !this.dialog )
			this.dialog = AppInjector.injector.get(MatDialog);

	}
	
	getConfigurationDialog() : any
	{
		return ContactCenterDialogComponent;
	}

	isSetupComplete(account: Account, cctagType?: string) : Promise<boolean>
	{
		this.initServices();
		return  this.adminService.getAccountTags(account.accountId).then( tags => {
			// apparently the tage type is changing as product name changes. ATM valid types are
			// 'CXAdvanced' | 'CXAdvancedTrial' | 'CXStandard' | 'cc-next-beta'
			// have agreement with CX team that I only need to check for the state of "FINISHED"
			console.log("CC isSetupComplete, tags", tags)
			if ( tags && tags.cx && tags.cx.state == 'FINISHED' )
				return true;
			else
				return false;
		})
		.catch( err => {
			console.error("Fetching tags fails, so there is no setup complete", err)
			return false;
		});
	}

	ccSetupCompleteTagTest( tags: any ): boolean
	{
		if ( tags && tags.cx && tags.cx.state == 'FINISHED' )
			return true;
		else
			return false;
	}

	ccEnabledOnUserTagTest( tags: any): boolean {
		if(tags && tags.cx ) {
			return true;
		}
		return false;
	}

	isEligibleForFreeTrial(account: Account, product: string): Promise<boolean>
	{
		this.initServices();
		return this.determineIfAccountIsEligibleForAFreeTrial( account, product );
	}

	async grantFreeTrial(
		account: Account, 
		product: any, 
		ratePlanChargeId: string, 
		ratePlanId: string ): Promise<any>
	{
		this.initServices();

		// we always will show a dialog to request the currency and number of seats
		// will we just set to true if it is the account admin. the error on the create will be used
		// to determine if there is no billing account 
		let billingAccountExists = true;
		let partnerAcctId = await this.orderService.determinePartnerAccountId( account );
		
		if ( this.appClaimsService.isAssumedRole() )
			billingAccountExists = await this.orderService.hasABillingAccount();

		let dialogData = {
			product: product,
			account: account,
			createBillingAccount: !billingAccountExists,
			subInfo: {
				partnerAccountId:partnerAcctId, 
				productRatePlanChargeId: ratePlanChargeId, 
				productRatePlanId: ratePlanId, 
				price: 0,
				period: 30,
				periodType: "Day",
				maxLicenses: 5,
				// this EULA is just a place holder. It is for MiTeam Meetings but will need to be changed when the correct one is available
				eulaUrl: "https://www.mitel.com/-/media/mitel/file/pdf/legal-docs/eula/miteam-meetings-v10-eula-3oct2019.pdf?modified=20191205154622"
			}
		};

		let dialogRef = this.dialog.open(FreeTrialDialogComponent,
		{
			disableClose: true,
			autoFocus: false,
			data: dialogData
		});
		return new Promise<boolean> ( (resolve, reject) => {
			dialogRef.afterClosed().subscribe( async result => {
				console.log("The result of the complete setup dialog", result);
				dialogRef = null;
				return resolve(result);
			});
		});
	}

	handleIntegrationStateChange(
		account: Account, 
		state: boolean, 
		productCode: string): Promise<any>
	{
		this.initServices();

		if ( state === false )
		{
			// display the dialog and wait for finish or error
			let dialogData = {
				product: {
					productCode: productCode
				},
				account: account
			};
			const dialogRef = this.dialog.open(ContactCenterRemoveDialogComponent, 
			{
				disableClose: true,
				autoFocus: false,
				data: dialogData
			});
			
			return dialogRef.afterClosed().toPromise().then(result => {
				if ( result == false ) //remove was not complete
					return false;
			});
		}
		else // true
		{
			return Promise.resolve();

			// leaving the commented code because it still is a work in progress.
			// there might be nothing we do here, but for now I will set the policy if 
			// the setup is not complete and it is not eligible for a free trial
			// return this.isSetupComplete(account, cctagType ).then( isComplete => {
			// 	if ( !isComplete )
			// 	{
			// 		return this.determineIfAccountIsEligibleForAFreeTrial(account, productCode).then( isEligible => {
			// 			if ( !isEligible )
			// 			{
			// 				console.log("Account was not eligible for the free trial", account);
			// 				// TBD - is the policy created if there is no subscription?
			// 				return this.adminService.addSpecifiedStatementToAccountPolicy( account, statement ).then( policy => {
			// 					if ( account['policyId'] == undefined ) // only need to update the account if there was no policy Id
			// 					{
			// 						let dataChanges = {
			// 							policyId: policy.policyId,
			// 						}
				
			// 			console.log("about to save the integration changes", dataChanges)
						
			// 						return this.adminService.updateAccount( account.accountId, dataChanges).then(result => {
			// 							this.accountsService.mergeAccountWithModel(result);
			// 							this.spinnerService.hideSpinner(); //bizarre that this needs to be done as the service does not do it
			// 							return;
			// 						});
			// 					}
			// 				});
			// 			}
			// 		})
			// 	}
			// })
		}
	}

	getLeftNavMenus( account: Account, ccTypeTag: string ): Promise<MenuItem[]>
	{
		this.initServices();
		return this.isSetupComplete( account, ccTypeTag ).then(complete => {
			if ( complete == false ) // setup has to be complete before we can display the menus
				return [];

			const dashboardItem = {
				name: 'ccDashboard',
				translationKey: 'About',
				id: 'ccdashboard',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/ccdashboard`						
			};
			
			const skillsGroupItem = {
				name: 'ccSkillsGroup',
				translationKey: 'SkillGroups',
				id: 'ccskillgroups',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/ccskills`						
			};

			const routingItem = {
				name: 'ccRouting',
				translationKey: 'Routing',
				id: 'ccroutings',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/ccrouting`						
			};
			
			const embeddedChatsItem = {
				name: 'ccEmbeddedChats',
				translationKey: 'EmbeddedChats',
				id: 'ccembeddedchats',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/ccembeddedchats`						
			};

			const reportsItem = {
				name: 'ccReports',
				translationKey: 'Reports',
				id: 'ccreports',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/ccreports`						
			};

			const codesItem = {
				name: 'ccCodes',
				translationKey: 'Codes',
				id: 'ccadvanced',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/cccodes`						
			};

			const templatesItem = {
				name: 'ccTemplates',
				translationKey: 'Templates',
				id: 'cctemplates',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/cctemplates`						
			};

			const integrationsItem = {
				name: 'ccIntegrations',
				translationKey: 'Integrations',
				id: 'ccintegrations',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/ccintegrations`						
			};

			const numbersItem = {
				name: 'ccNumbers',
				translationKey: 'Numbers',
				id: 'ccnumberss',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/ccnumbers`						
			};

			const settingsItem = {
				name: 'ccSettings',
				translationKey: 'Settings',
				id: 'ccsettings',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/ccsettings`						
			};

			const helpItem = {
				name: 'ccHelp',
				translationKey: 'Help',
				id: 'cchelpcx',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: this.translateService.instant('integrations.cc-next.help-url'),
				openInNewWindow: true,				
			};

			const callCenterItem = {
				name: 'callCenter',
				translationKey: 'ContactCenter',
				id: 'callcenter',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: '/norouting',
				iconActive: '../../../../assets/images/contactCenter_active.svg',
				iconInactive: '../../../../assets/images/contactCenter_inactive.svg',
				children: [
					dashboardItem,
					skillsGroupItem,
					routingItem,
					embeddedChatsItem,
					codesItem,
					templatesItem,
					integrationsItem,
					numbersItem,
					settingsItem,
					helpItem
				]						
			};
			console.log("return left nav menus", [callCenterItem])
			return Promise.resolve( [callCenterItem] );
		});
	}

	// it appears we need to test these conditions in several places, so separate function
	// caller is responsible for any spinners they want to display
	// caller is responsible to handle any errors generated as appropriate to their situation
	determineIfAccountIsEligibleForAFreeTrial( account: Account, product: string ): Promise<boolean>
	{
		return Promise.resolve(false);
		// free trial is being removed for now, but is it long term? keep the code for now
		// need to determine if the customer account has any existing CX subscriptions
		// return this.orderService.doesCustomerAccountHaveExistingProductSubs(environment.subscriptionIds.ContactCenter).then(existingSubs => {
		// 	if ( existingSubs === true )
		// 	{
		// 		console.log("THere are existing contact center subscriptions for this account so no free trial", account);
		// 		return false;
		// 	}
		// 	else
		// 	{
		// 		return true;
		// 	}
		// });
	}
}
